import {useSelector} from 'react-redux';
import {Selector} from '../../state/selector';
import Typography from '@mui/material/Typography';
import {Box, Card} from '@mui/material';
import QRCode from 'react-qr-code';
import {ContentCopy, Image as ImageIcon, InsertDriveFile, Newspaper, PictureAsPdf} from '@mui/icons-material';
import {PDFDocument} from 'pdf-lib';
import {useEffect, useRef} from 'react';

const WIDGET_BASE_URL = process.env.REACT_APP_WIDGET_BASE_URL ||
    'https://web.staging-dehoga-analytics.inseciacloud.net';

const cardStyle = {
    width: 300,
    padding: 3,
    borderRadius: 2,
    feDropShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: {sm: '338px', xs: 'auto'},
};

const downloadTextStyle = {
    color: 'rgba(0,49,248,0.64)',
    fontWeight: 'bold',
    cursor: 'pointer',
};

const iconStyle = {
    fontSize: '100px',
    margin: '15px 0',
};

// Helper: Convert QR SVG to PNG Data URL
const svgToPngDataUrl = (svgElement: SVGElement): Promise<string> => {
    const svgData = new XMLSerializer().serializeToString(svgElement);
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx!.drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/png'));
        };
        img.src = `data:image/svg+xml;base64,${btoa(svgData)}`;
    });
};

// Download a new PDF with QR code overlaid at bottom right
const downloadFlyer = async (company: any) => {
    const pdfUrl = '/2026%20Wahl%20Lieblingslokal-Flyer.pdf';
    // Fetch and load the PDF
    const existingPdfBytes = await fetch(pdfUrl).then(res => res.arrayBuffer());
    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    const page = pdfDoc.getPages()[0];

    // Get QR as PNG Data URL
    const qrSvg = document.getElementById(`qrCode-${company?.id}`);
    if (!(qrSvg instanceof SVGElement)) {
        alert('QR-Code not found');
        return;
    }
    const qrPngDataUrl = await svgToPngDataUrl(qrSvg);

    // Embed QR PNG in PDF
    const pngImage = await pdfDoc.embedPng(qrPngDataUrl);
    const qrSize = 120;
    page.drawImage(pngImage, {
        x: 84, // adjust as needed
        y: 40, // adjust as needed
        width: qrSize,
        height: qrSize,
    });

    // Download modified PDF
    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download =
        `2026 Wahl Lieblingslokal-Flyer-${(company?.restaurantName || 'Restaurant').replace(/\s+/g, '-')}.pdf`;
    link.click();
};

const downloadQR = (canvasId: string, fileName: string) => {
    const svg = document.getElementById(canvasId);
    if (svg) {
        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const img = new Image();
        img.onload = () => {
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);
            const pngFile = canvas.toDataURL('image/png');
            const downloadLink = document.createElement('a');
            downloadLink.download = fileName;
            downloadLink.href = `${pngFile}`;
            downloadLink.click();
        };
        img.src = `data:image/svg+xml;base64,${btoa(svgData)}`;
    }
};

const downloadFile = (fileName: string, src: string, fileType?: string) => {
    const downloadLink = document.createElement('a');
    downloadLink.download = fileName;
    downloadLink.href = src;
    downloadLink.target = '_blank';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
};

/* ── Live Widget (loads actual script via iframe) ── */
function LiveWidget({ companyId }: { companyId?: string }) {
    const iframeRef = useRef<HTMLIFrameElement>(null);

    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe || !companyId) return;

        const html = `<!DOCTYPE html><html><head>
            <meta charset="utf-8"><style>body{margin:0;font-family:system-ui,sans-serif;}</style></head><body>
            <div class="bg-widget" data-company-id="${companyId}"></div>
            <script src="${WIDGET_BASE_URL}/widget.js" async></script>
            </body></html>`;

        iframe.srcdoc = html;
    }, [companyId]);

    return (
        <Box sx={{ maxWidth: 620, mx: 'auto', mb: 3 }}>
            <iframe
                ref={iframeRef}
                style={{ width: '100%', height: 180, border: 'none', overflow: 'hidden' }}
                title={'Widget Preview'}
            />
        </Box>
    );
}


function WidgetPreview({ company }: { company: any }) {

    const snippetCode = [
        `<div class="bg-widget" data-company-id="${company?.id}">`,
        '</div>',
        `<script src="${WIDGET_BASE_URL}/widget.js" async>`,
        '</script>',
    ].join('\n');

    const iframeCode = [
        '<iframe style="width:100%;max-width:620px; height:180px;border:none;overflow:hidden;"',
        ' title="Brandenburger Gastlichkeit Widget"',
        ' srcdoc=\'<!DOCTYPE html><html><head>',
        '<meta charset="utf-8">',
        '<style>body{margin:0;font-family:system-ui, sans-serif;}</style></head><body>',
        '<div class="bg-widget"',
        ` data-company-id="${company?.id}"></div> <script src="${WIDGET_BASE_URL}/widget.js"`,
        ' async></script></body></html>\'>',
        '</iframe>',
    ].join('');

    const handleCopy = () => {
        navigator.clipboard.writeText(snippetCode);
    };

    const handleCopyIframe = () => {
        navigator.clipboard.writeText(iframeCode);
    };

    return (
        <Box sx={{ width: '100%', maxWidth: 680, mx: 'auto', mt: 4 }}>
            <Typography variant={'h5'} sx={{ mb: 2, textAlign: 'center' }}>
                Einbettbares Widget
            </Typography>
            <Typography variant={'body2'} sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
                Binden Sie dieses Widget auf Ihrer Website ein, um Ihren Gastlichkeits-Score zu zeigen.
            </Typography>

            {/* Live widget via script */}
            <Typography variant={'subtitle2'} sx={{ fontWeight: 600, mb: 1 }}>
                Live Widget (Script)
            </Typography>
            <LiveWidget companyId={company?.id} />

            {/* Embed code */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                <Typography
                    variant={'subtitle2'}
                    sx={{ fontWeight: 600 }}
                >
                    Einbettungscode
                </Typography>
                <Box
                    onClick={handleCopy}
                    sx={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        color: 'rgba(0,49,248,0.64)',
                        fontSize: 12,
                        fontFamily: 'system-ui',
                        fontWeight: 600,
                        '&:hover': { opacity: 0.7 },
                    }}
                >
                    <ContentCopy sx={{ fontSize: 16 }} />
                    Kopieren
                </Box>
            </Box>
            <Box
                sx={{
                    background: '#f5f7f9',
                    borderRadius: 2,
                    p: 2,
                    fontFamily: 'monospace',
                    fontSize: 13,
                    lineHeight: 1.6,
                    wordBreak: 'break-all',
                    color: '#334155',
                    border: '1px solid #e2e8f0',
                }}
            >
                <code>{snippetCode}</code>
            </Box>
            <Typography
                variant={'caption'}
                sx={{
                    mt: 1,
                    display: 'block',
                    color: 'text.secondary',
                }}
            >
                {'Fügen Sie diesen Code in den HTML-Quellcode '
                    + 'Ihrer Website ein. (Haben Sie Probleme? '
                    + 'Versuchen Sie es mit einem '}
                <Box
                    component={'span'}
                    onClick={handleCopyIframe}
                    sx={{
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        verticalAlign: 'middle',
                        gap: 0.3,
                        color: 'rgba(0,49,248,0.64)',
                        fontWeight: 600,
                        '&:hover': { opacity: 0.7 },
                    }}
                >
                    <ContentCopy sx={{ fontSize: 14 }} />
                    iFrame
                </Box>
                )
            </Typography>
        </Box>
    );
}

const DownloadPage = () => {
    const hasNormalCompanyAccess = useSelector(Selector.User.hasNormalCompanyAccess);
    const hasTesterCompanyAccess = useSelector(Selector.User.hasTesterCompanyAccess);

    const company = useSelector(Selector.Company.selectedCompany);
    // QR code and flyer are available if marketing is booked for ANY version
    const hasMarketing = company?.bookings
        ? Object.values(company.bookings).some(b => b.marketing)
        : false;

    if (!hasNormalCompanyAccess && !hasTesterCompanyAccess) {
        return <Typography> No Access to this Company </Typography>;
    }

    return (
        <Box sx={{height: '100%', overflow: 'scroll'}}>
            <Typography variant={'h1'} sx={{paddingBottom: '20px'}}>
                Ihre Downloads
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    gap: 4,
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '50vh',
                    paddingBottom: hasMarketing ? '0px' : '250px',
                }}
            >
                {/* QR-Code */}
                {hasMarketing && (
                    <Card sx={cardStyle}>
                        <Typography
                            variant={'subtitle2'}
                            sx={{fontWeight: 600, mb: 1, textAlign: 'center'}}
                        >
                            Lieblingslokal
                        </Typography>
                        <QRCode
                            id={`qrCode-${company?.id}`}
                            size={252}
                            style={{height: 'auto', maxWidth: '100%', width: '100%'}}
                            value={'https://app.brandenburger-gastlichkeit.de/vote/' + company?.id}
                            level={'H'}
                            viewBox={'0 0 256 256'}
                        />
                        <br />
                        <span
                            style={downloadTextStyle}
                            onClick={() => downloadQR(
                                `qrCode-${company?.id}`,
                                `qrCode-${(company?.restaurantName || 'Lieblings-Lokal')
                                    .replace(/\s+/g, '-')}.png`)}
                        >
                            Download QR-Code
                        </span>
                        <Box
                            onClick={() => {
                                navigator.clipboard.writeText(
                                    'https://app.brandenburger-gastlichkeit.de/vote/' + company?.id,
                                );
                            }}
                            sx={{
                                mt: 1,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.5,
                                color: 'rgba(0,49,248,0.64)',
                                fontSize: 12,
                                fontWeight: 600,
                                '&:hover': {opacity: 0.7},
                            }}
                        >
                            <ContentCopy sx={{fontSize: 16}} />
                            Link kopieren
                        </Box>
                    </Card>
                )}
                {hasMarketing && (
                    <Card sx={cardStyle}>
                        <Typography variant={'h5'} sx={{wordBreak: 'break-word', textAlign: 'center'}}>
                            Flyer fürs Restaurant
                        </Typography>
                        <Newspaper sx={iconStyle} />
                        <span
                            style={downloadTextStyle}
                            onClick={() => downloadFlyer(company)}
                        >
                            Download Flyer
                        </span>
                    </Card>
                )}

                {/* Download Files */}
                {downloadData.map((data, index) => (
                    <Card key={index} sx={cardStyle}>
                        <Typography variant={'h5'} sx={{wordBreak: 'break-word', textAlign: 'center'}}>
                            {data.fileName.replaceAll('-', ' ').replaceAll('_', ' ')}
                        </Typography>
                        {data.fileType?.includes('pdf') ?
                            (<PictureAsPdf sx={iconStyle} />) :
                            (data.fileType?.includes('jpg') ||
                                data.fileType?.includes('jpeg') ||
                                data.fileType?.includes('png')) ?
                                (<ImageIcon sx={iconStyle} />) :
                                (<InsertDriveFile sx={iconStyle} />)
                        }
                        <span
                            style={downloadTextStyle}
                            onClick={() => downloadFile(data.fileName, data.src, data.fileType)}
                        >
                            Download
                        </span>
                    </Card>
                ))}

                {!hasMarketing && downloadData.length < 1 && (
                    <Typography variant={'h4'} sx={{textAlign: 'center', width: '100%'}}>
                        Es sind keine Downloads verfügbar.
                    </Typography>
                )}

            </Box>

            {/* Widget embed section */}
            {hasMarketing && company && (
                <WidgetPreview company={company} />
            )}
        </Box>
    );
};

const downloadData: { fileName: string, src: string, fileType?: string }[] = [];

export default DownloadPage;
